import React, { forwardRef, lazy, useEffect, useMemo, useCallback, memo, useState } from 'react'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import clsx from 'clsx'
import 'react-phone-number-input/style.css'
import { SuspenseLoader } from '../../../../../../Web/WebLoader'
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input'
const PhoneInput = lazy(() => import('react-phone-number-input/min'))
import { fetchGlobalBrowserVariable, GLOBAL_VAR_KEYS } from '../../../../../../lib/globalUtils'
import uuid from 'uuid'

const getSelectedOption = (options, value) => {
  const firstMatchingOption = options.find((opt) => opt.value === value)
  return firstMatchingOption
}

const applyParentPositionOnElementOrClassStyle = (popupElement, parentPosition, zoomFactor, visibility) => {
  if (!popupElement) return

  popupElement.style.removeProperty('left')
  popupElement.style.removeProperty('top')
  popupElement.style.cssText += `left: ${parentPosition.left / zoomFactor}px !important;`
  popupElement.style.cssText += `top: ${parentPosition.bottom / zoomFactor}px !important;`

  popupElement.style.removeProperty('display')
  popupElement.style.cssText += `display: ${visibility ? 'block' : 'none'};`
}

const movePopupToParentLocationAndChangeVisibility = (uniqueIdentifier, customStylesName, visibility) => {
  let zoomString = getComputedStyle(document.body).zoom
  if (!zoomString) zoomString = '1'

  const zoomFactor = parseFloat(zoomString)
  const parentElement = document.getElementsByName(uniqueIdentifier)[0]
  if (!parentElement) return

  const parentPosition = parentElement.getBoundingClientRect()
  const popupElement = document.getElementsByClassName(customStylesName)[0]
  if (!popupElement) return

  const styleElement = document.getElementById(`${uniqueIdentifier}-styles`)
  const ruleClass = styleElement?.sheet?.cssRules[0]
  applyParentPositionOnElementOrClassStyle(ruleClass, parentPosition, zoomFactor, visibility)

  applyParentPositionOnElementOrClassStyle(popupElement, parentPosition, zoomFactor, visibility)
  if (visibility) popupElement.children?.[0]?.focus()
}

const handlePopupAndPushToEventLoop = (name, customStylesName, isPopupVisible) => {
  setTimeout(() => {
    movePopupToParentLocationAndChangeVisibility(name, customStylesName, isPopupVisible)
  }, 0)
}

const CountrySelectBaseComponent = memo(function CountrySelectBaseComponent({
  value,
  onChange,
  options,
  disabled,
  readOnly,
  iconComponent: Icon,
  customStylesName,
  name,
  ...rest
}) {
  const onSelectChange = useCallback(
    (event) => {
      const value = event.target.value
      onChange(value)
    },
    [onChange]
  )

  const optionsTemplate = useMemo(() => {
    return options.map(({ value, label }) => (
      <MenuItem key={value || ''} value={value || ''}>
        <Icon aria-hidden country={value} label={label} aspectRatio={1} />
        {label}
        {value && (
          <span className="ft--theme--FormField--phone--option-country-code">(+{getCountryCallingCode(value)})</span>
        )}
      </MenuItem>
    ))
  }, [options, getCountryCallingCode])

  const isAutoScaleZoomApplied = fetchGlobalBrowserVariable(GLOBAL_VAR_KEYS.AUTO_SCALE_ZOOM)

  const autoScaleZoomAppliedHandleOpen = () => {
    handlePopupAndPushToEventLoop(name, customStylesName, true)
  }

  const autoScaleZoomAppliedHandleClose = () => {
    handlePopupAndPushToEventLoop(name, customStylesName, false)
  }

  const isInIframe = useMemo(() => window !== window.parent, [])

  const disableIframeFlicker = useMemo(
    () =>
      /** MUI Select uses Menu which uses Popover
         There is a known issue where opening a Material-UI Popover within an iframe
         causes the page to scroll to the top.
         This behavior occurs because the Popover component attempts to automatically focus
         itself upon opening, which can lead to unintended scrolling within the iframe
         **/
      ({
        disableAutoFocus: true,
        disableAutoFocusItem: true,
        disableEnforceFocus: true,
        autoFocus: false,
      }),
    []
  )
  return (
    <Select
      {...rest}
      style={{ width: 0 }}
      disabled={disabled || readOnly}
      readOnly={readOnly}
      value={value || ''}
      onChange={onSelectChange}
      MenuProps={{
        ...(isInIframe && disableIframeFlicker),
        anchorOrigin: {
          horizontal: 'left',
          vertical: 'bottom',
        },
        getContentAnchorEl: null,
        PaperProps: {
          className: isAutoScaleZoomApplied ? customStylesName : 'ft--theme--FormField--phone--options-paper',
        },
        classes: { list: 'ft--theme--FormField--phone--options-paper-zoom-compatible-ul' },
      }}
      name={name}
      {...(isAutoScaleZoomApplied
        ? { onOpen: autoScaleZoomAppliedHandleOpen, onClose: autoScaleZoomAppliedHandleClose }
        : {})}>
      {optionsTemplate}
    </Select>
  )
})

const CountrySelectComponent = memo(function CountrySelectComponent({
  value,
  options,
  className,
  iconComponent: Icon,
  customStylesName,
  ...rest
}) {
  const selectedOption = useMemo(() => {
    return getSelectedOption(options, value)
  }, [options, value])

  return (
    <div className="PhoneInputCountry">
      <CountrySelectBaseComponent
        {...rest}
        value={value}
        options={options}
        iconComponent={Icon}
        className={clsx('PhoneInputCountrySelect', className)}
        customStylesName={customStylesName}
      />

      <Icon aria-hidden country={value} label={selectedOption?.label || ''} />

      <div className="PhoneInputCountrySelectArrow" />
    </div>
  )
})

const CustomInput = forwardRef((props, ref) => (
  <input {...props} dir="ltr" pattern={'[0-9]*'} inputMode={'numeric'} ref={ref} />
))

export const PhoneInputControl = (props) => {
  const {
    onChange,
    value,
    className,
    isCountryLocked,
    isPreview,
    readOnly,
    defCountry: providedDefCountry,
    isAutoCountryCode,
    ...other
  } = props

  const [uniqueIdentifier] = useState(uuid())
  const [customStylesName] = useState(`ft--theme--FormField--phone--options-paper-zoom-compatible-${uniqueIdentifier}`)
  const [customStyles] = useState(`.${customStylesName}{
      margin-top: 10px;
      padding: 8px 0;
      box-shadow: 0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12);
      border-radius: 4px;
      transition: none !important;
      position: absolute !important;
      top: unset;
      left: unset;
      display: none;
  }`)

  let defCountry = useMemo(() => {
    if (value) {
      try {
        let val = String(value)
        if (val.length < 5) {
          val += '0000'
        }
        let parseCountry = parsePhoneNumber(val)
        if (parseCountry && parseCountry.country) {
          return parseCountry.country
        }
      } catch (err) {
        console.error(err)
      }
    }
    return providedDefCountry
  }, [providedDefCountry, value])

  useEffect(() => {
    if (!defCountry || !value) return
    const countryCode = getCountryCallingCode(defCountry)
    if (countryCode && (value || '')[0] !== '+') {
      onChange({ target: { value: '+' + countryCode + value } })
    }
  }, [value, defCountry])

  return (
    <SuspenseLoader>
      <style id={`${uniqueIdentifier}-styles`}>{customStyles}</style>
      <PhoneInput
        name={uniqueIdentifier}
        value={String(value)}
        displayInitialValueAsLocalNumber
        onChange={(phone) => {
          onChange({ target: { value: phone || '' } })
        }}
        numberInputProps={{
          className: className,
          style: other.style,
        }}
        inputComponent={CustomInput}
        countrySelectComponent={CountrySelectComponent}
        countrySelectProps={{ name: uniqueIdentifier, customStylesName }}
        {...(defCountry
          ? {
              defaultCountry: defCountry,
            }
          : {})}
        {...(!isPreview
          ? {
              countries: defCountry ? [defCountry] : [],
              addInternationalOption: !defCountry,
            }
          : {})} // forces flag display
        {...(isCountryLocked
          ? {
              countries: [defCountry],
              addInternationalOption: false,
              countryCallingCodeEditable: false,
              countrySelectProps: {
                disabled: true,
              },
            }
          : {})}
        {..._.omit(other, ['decimalDigits', 'decimalSymbol', 'thousandSymbol'])}
        readOnly={readOnly}
        {...(isAutoCountryCode ? { international: true } : {})}
        {...(other.disabled || readOnly ? { countrySelectProps: { disabled: other.disabled || readOnly } } : {})}
        international
      />
    </SuspenseLoader>
  )
}
